import { useEffect, useRef, useState } from 'react';

// Keep the continuous fill on the compositor; React only needs the crossed step.
export const useSectionProgress = (sectionRef, fillRef, stepCount) => {
  const [activeStep, setActiveStep] = useState(-1);
  const activeStepRef = useRef(-1);

  useEffect(() => {
    let frameId = null;
    let disposed = false;
    let previousProgress = null;

    const updateProgress = () => {
      frameId = null;
      if (disposed || !sectionRef.current) return;

      const rect = sectionRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const startOffset = viewportHeight * 0.3;
      const distance = rect.height + startOffset - viewportHeight * 0.5;
      const progress = distance > 0
        ? Math.min(Math.max((startOffset - rect.top) / distance, 0), 1)
        : 0;

      if (fillRef.current && progress !== previousProgress) {
        fillRef.current.style.transform = `translateX(-50%) scaleY(${progress})`;
        previousProgress = progress;
      }

      let nextActiveStep = -1;
      for (let index = 0; index < stepCount; index += 1) {
        if (progress >= (index + 0.5) / stepCount) nextActiveStep = index;
        else break;
      }

      if (nextActiveStep !== activeStepRef.current) {
        activeStepRef.current = nextActiveStep;
        setActiveStep(nextActiveStep);
      }
    };

    const scheduleUpdate = () => {
      if (!disposed && frameId === null) {
        frameId = window.requestAnimationFrame(updateProgress);
      }
    };

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate, { passive: true });
    scheduleUpdate();

    return () => {
      disposed = true;
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, [sectionRef, fillRef, stepCount]);

  return activeStep;
};

export default useSectionProgress;
